/* Racepack Fuel Planner v4.12.3 — carb-gated, sodium-led in-range recommendation ranking; no customer data storage. */
(function (global) {
  'use strict';

  const FUEL_FORMATS = new Set(['gel', 'chew', 'drink', 'bar', 'drinkmix']);
  const HYDRATION_FORMATS = new Set(['tablet', 'capsule', 'electrolyte', 'drinkmix']);

  function number(value, min, max, label) {
    const n = Number(value);
    if (value === '' || value == null || !Number.isFinite(n) || n < min || n > max) {
      throw new Error(label);
    }
    return n;
  }

  function optionalPositiveInteger(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isInteger(n) && n >= 1 ? n : NaN;
  }

  function validTriState(value) {
    return value === null || value === true || value === false;
  }

  function servingLimit(product) {
    const limits = [product?.maxServings, product?.maxServingsPerDay]
      .filter(v => Number.isInteger(v) && v >= 1);
    return limits.length ? Math.min(...limits) : null;
  }

  function servingLimitText(product) {
    const parts = [];
    if (Number.isInteger(product?.maxServings) && product.maxServings >= 1) {
      parts.push(`${product.maxServings} per session`);
    }
    if (Number.isInteger(product?.maxServingsPerDay) && product.maxServingsPerDay >= 1) {
      parts.push(`${product.maxServingsPerDay} per day`);
    }
    return parts.join(' and ');
  }

  function planCarbRange(plan, extraCarbs = 0) {
    const total = Number.isFinite(Number(plan?.total)) ? Number(plan.total) : 0;
    const totalLow = Number.isFinite(Number(plan?.totalLow)) ? Number(plan.totalLow) : total;
    const totalHigh = Number.isFinite(Number(plan?.totalHigh)) ? Number(plan.totalHigh) : total;
    const external = Math.max(0, Number(plan?.external || 0) + Number(extraCarbs || 0));

    return {
      total,
      totalLow: Math.min(totalLow, totalHigh),
      totalHigh: Math.max(totalLow, totalHigh),
      external,
      remaining: Math.max(0, total - external),
      remainingLow: Math.max(0, Math.min(totalLow, totalHigh) - external),
      remainingHigh: Math.max(0, Math.max(totalLow, totalHigh) - external)
    };
  }

  function carbRangeMetrics(plan, fuelCarbs = 0, extraCarbs = 0) {
    const range = planCarbRange(plan, extraCarbs);
    const combined = range.external + Math.max(0, Number(fuelCarbs || 0));
    const below = Math.max(0, range.totalLow - combined);
    const above = Math.max(0, combined - range.totalHigh);
    const rangeDistance = below + above;
    const centerDistance = Math.abs(combined - range.total);
    const hours = Math.max(0.001, Number(plan?.minutes || 0) / 60);

    return {
      ...range,
      combined,
      below,
      above,
      rangeDistance,
      centerDistance,
      achievedRate: combined / hours,
      withinRange: rangeDistance === 0
    };
  }

  function caffeineReviewThreshold(plan) {
    const weight = Number(plan?.weight);
    return Number.isFinite(weight) && weight > 0
      ? Math.round(weight * 3)
      : 200;
  }

  function calculate(input) {
    input.effort = input.effort || 'steady';
    input.sweat = input.sweat || 'unsure';

    let minutes = number(input.minutes, 15, 720, 'Enter a duration between 15 minutes and 12 hours.');
    const bands = {
      '60-90': [60, 90, 75],
      '91-120': [91, 120, 105],
      '121-150': [121, 150, 135],
      '151+': [151, 720, 180]
    };

    const band = input.sport === 'running' && input.distance === '21.1' && input.band
      ? bands[input.band]
      : null;

    if (input.band && input.sport === 'running' && input.distance === '21.1' && !band) {
      throw new Error('Choose a valid half-marathon finish-time band.');
    }

    if (band) {
      if (input.band === '151+' && (input.finishMinutes === '' || input.finishMinutes == null)) {
        throw new Error('For 151+ minutes, enter your expected finish time.');
      }
      minutes = input.finishMinutes === '' || input.finishMinutes == null
        ? band[2]
        : number(
            input.finishMinutes,
            band[0],
            band[1],
            'Exact finish time must fall within your selected band.'
          );
    }

    const external = number(input.external || 0, 0, 1500, 'Enter a valid total for fuel already planned.');

    const choices = {
      sport: ['running', 'cycling', 'hyrox', 'swimming', 'gym', 'other'],
      purpose: ['race', 'training', 'completion', 'performance'],
      experience: ['new', 'practised'],
      caffeine: ['no', 'yes'],
      sensitive: ['yes', 'no'],
      access: ['yes', 'no'],
      water: ['yes', 'no'],
      format: ['any', 'gel', 'chew', 'drink', 'bar', 'drinkmix'],
      diet: ['none', 'vegan', 'gluten', 'dairy'],
      conditions: ['normal', 'hot'],
      brand: [
        'any', 'maurten', 'sis', 'gu', 'aminovital', 'tailwind', 'saltstick',
        'crampfix', 'nuun', 'skratch', 'high5', 'lmnt', 'clif', 'gatorade',
        'myprotein', 'beetit', 'honeystinger', 'ote', 'hammer', 'cadence',
        'pocari', 'injelly', 'other'
      ],
      effort: ['easy', 'steady', 'hard'],
      sweat: ['light', 'moderate', 'heavy', 'unsure']
    };

    Object.entries(choices).forEach(([key, values]) => {
      if (!values.includes(input[key])) throw new Error('Please complete ' + key + '.');
    });

    const budget = input.budget === '' || input.budget == null
      ? null
      : number(input.budget, 1, 1000, 'Enter a fuel-pack budget from 1 to 1,000, or leave it blank.');

    const externalCaffeine = number(
      input.externalCaffeine || 0,
      0,
      1000,
      'Enter known caffeine from other sources between 0 and 1,000 mg.'
    );

    const tolerated = input.tolerated === '' || input.tolerated == null
      ? null
      : number(
          input.tolerated,
          0,
          90,
          'Previously tolerated intake must be between 0 and 90 g/h.'
        );

    let low = 0;
    let high = 0;
    let target = 0;
    const notes = [];

    if (input.sport === 'gym') {
      notes.push('This planner does not prescribe during-workout carbohydrate for gym sessions. Start with normal meals, water and your training needs.');
    } else if (minutes <= 60) {
      notes.push('For a session up to one hour, additional sports fuel is usually optional when starting well fed.');
    } else {
      [low, high] = minutes <= 150 ? [30, 60] : [60, 90];
      target = input.experience === 'new' || input.sensitive === 'yes'
        ? 30
        : (minutes <= 150 ? 45 : 60);

      if (input.purpose === 'training') target = Math.min(target, 45);

      if (tolerated != null) {
        target = input.experience === 'practised' &&
                 input.sensitive === 'no' &&
                 input.purpose === 'race' &&
                 input.effort !== 'easy' &&
                 minutes > 150
          ? Math.min(tolerated, high)
          : Math.min(target, tolerated);
      }

      if (input.effort === 'easy') {
        target = Math.min(target, 30);
        notes.push('Easy-session mode uses a conservative starting target; adjust it with your tested training plan.');
      }

      if (input.effort === 'hard') {
        notes.push('A hard effort does not automatically override your practised carbohydrate tolerance.');
      }

      if (target < low) {
        notes.push('Your conservative starting target is below the general endurance range. Practise and review it before relying on it for a long race.');
      }
    }

    let desired = target;

    if (['completion', 'performance'].includes(input.purpose) && input.sport !== 'gym') {
      // Racepack policy within published duration guidance; not a validated goal-specific dose.
      desired = minutes <= 45
        ? 0
        : minutes <= 75
          ? (input.purpose === 'performance' ? 20 : 0)
          : minutes <= 150
            ? (input.purpose === 'performance' ? 60 : 30)
            : (input.purpose === 'performance' ? 75 : 45);

      target = desired;

      if (input.experience === 'new' || input.sensitive === 'yes') target = Math.min(target, 30);
      if (input.effort === 'easy') target = Math.min(target, 30);
      if (target > 60 && (input.tolerated === '' || input.tolerated == null)) target = 60;

      if (tolerated != null) {
        target = Math.min(target, tolerated);
      }

      if (minutes <= 75) {
        low = 0;
        high = 0;
        notes.push('At 45–75 minutes, guidance favours small amounts or mouth sensing rather than a fixed hourly dose. The optional 20 g/h performance reference is a Racepack planning assumption.');
      }

      if (target < desired) {
        notes.push('The ' + desired + ' g/h goal reference was reduced to ' + target + ' g/h to respect your experience, sensitivity or confirmed tolerance. Performance mode cannot override these limits.');
      }

      if (minutes > 150 && input.purpose === 'completion') {
        notes.push('The 45 g/h completion reference is a conservative Racepack starting policy below the longer-event performance range. Review and train your fuelling before a long race.');
      }
    }

    if (band && (input.finishMinutes === '' || input.finishMinutes == null)) {
      notes.push('Using ' + minutes + ' minutes as a representative time for the ' + input.band + ' minute band. Enter an exact time to refine quantities.');
    }

    if (input.sensitive === 'yes') {
      notes.push('Sensitivity does not identify a guaranteed stomach-safe product. Test the exact product and amount in training.');
    }

    if (input.sweat === 'heavy') {
      notes.push('Heavy sweating: confirm regular fluid access and a tested electrolyte strategy. Sweat level alone does not determine a sodium dose.');
    }

    if (input.caffeine === 'no' && externalCaffeine > 0) {
      notes.push('You selected no caffeine for the recommended pack, but other planned sources contain caffeine. Review those sources too.');
    }

    if (input.sport === 'swimming') {
      notes.push('Use planned poolside or feed breaks; never consume fuel while actively swimming.');
    }

    if (input.sport === 'hyrox') {
      notes.push('Check event rules and accessible transitions. The timing below is an illustration, not a station-by-station race prescription.');
    }

    if (input.sport === 'other') {
      notes.push('This is a general endurance estimate. Check whether the suggested timing fits your activity.');
    }

    if (input.access === 'no' && target > 0) {
      notes.push('You have no confirmed fuel access. Confirm how you will carry or obtain fuel before using this plan.');
    }

    let rateLow = 0;
    let rateHigh = 0;
    const targetWasReduced = target < desired;

    if (target > 0) {
      if (minutes <= 75 || low === 0 || high === 0) {
        rateLow = 0;
        rateHigh = target;
      } else if (targetWasReduced) {
        // Experience, sensitivity, easy-effort or confirmed-tolerance guards are
        // treated as an upper cap. Where the reduced target still sits inside the
        // published range, use the published lower edge rather than encouraging
        // intake below it. If the cap itself is below the published range, keep
        // that conservative cap as the single planning rate.
        rateLow = Math.min(low, target);
        rateHigh = target;
      } else if (target < low) {
        // A deliberately conservative goal reference below the published duration
        // range can still use a narrow band that approaches the evidence range.
        rateLow = target;
        rateHigh = Math.min(low, target + 15);
      } else if (target > high) {
        rateLow = Math.max(high, target - 15);
        rateHigh = target;
      } else {
        rateLow = Math.max(low, target - 15);
        rateHigh = Math.min(high, target + 15);
      }

      if (tolerated != null) rateHigh = Math.min(rateHigh, tolerated);
      if (rateHigh < rateLow) rateLow = rateHigh;
    }

    const total = Math.round(target * minutes / 60);
    const totalLow = Math.round(rateLow * minutes / 60);
    const totalHigh = Math.round(rateHigh * minutes / 60);

    if (external > totalHigh) {
      notes.push('Your already-planned carbohydrate exceeds the upper edge of this starting planning range. Review all sources together.');
    } else if (external >= totalLow && totalHigh > 0) {
      notes.push('Your already-planned carbohydrate already reaches this starting planning range; additional fuel may not be needed.');
    }

    const fluidRate = input.fluidRate === '' || input.fluidRate == null
      ? (input.conditions === 'hot' ? 0.7 : 0.5)
      : number(input.fluidRate, 0.2, 1.5, 'Tested fluid plan must be 0.2–1.5 L/h.');

    const sodiumConcentration = input.sodiumConcentration === '' || input.sodiumConcentration == null
      ? 600
      : number(input.sodiumConcentration, 200, 1500, 'Planned sodium concentration must be 200–1,500 mg/L.');

    const externalSodium = number(
      input.externalSodium || 0,
      0,
      20000,
      'Enter known sodium already planned in mg.'
    );

    let sodiumRate = fluidRate * sodiumConcentration;
    let sodiumRange = null;
    const simpleHydration = input.hydrationMode === 'simple';

    if (simpleHydration) {
      // Transparent Racepack starting policy. Weather/intensity cannot measure sweat losses.
      const rates = {
        normal: { easy: 300, steady: 360, hard: 450 },
        hot: { easy: 390, steady: 480, hard: 600 }
      };
      sodiumRate = minutes <= 60 || input.sport === 'gym' ? 0 : rates[input.conditions][input.effort];
      sodiumRange = [Math.round(sodiumRate * 0.8), Math.round(sodiumRate * 1.2)];
    }

    const sodiumTotal = Math.round(sodiumRate * minutes / 60);
    const ownedGels = number(input.ownedGels || 0, 0, 500, 'Enter a whole number of owned gels from 0 to 500.');
    if (!Number.isInteger(ownedGels)) throw new Error('Owned gels must be a whole number.');

    if (input.weight === '' || input.weight == null) {
      throw new Error('Enter your body mass to continue.');
    }
    if (input.height === '' || input.height == null) {
      throw new Error('Enter your height to continue.');
    }

    const weight = number(input.weight, 30, 200, 'Body mass must be 30–200 kg.');
    const height = number(input.height, 100, 230, 'Height must be 100–230 cm.');

    const bmi = Math.round(weight / ((height / 100) ** 2) * 10) / 10;

    const km = input.distance === '21.1'
      ? 21.0975
      : input.distance === '42.2'
        ? 42.195
        : ['5', '10'].includes(input.distance)
          ? Number(input.distance)
          : null;

    const energyBurn = weight && input.sport === 'running' && km
      ? Math.round(weight * km)
      : null;

    return {
      minutes,
      band: band ? input.band : null,
      bandBounds: band ? [band[0], band[1]] : null,
      desired,
      low,
      high,
      target,
      rateLow,
      rateHigh,
      total,
      totalLow,
      totalHigh,
      fuelCalories: total * 4,
      fuelCaloriesLow: totalLow * 4,
      fuelCaloriesHigh: totalHigh * 4,
      energyBurn,
      weight,
      height,
      bmi,
      external,
      ownedGels,
      simpleHydration,
      sodiumRate,
      sodiumRange,
      sodiumTotalRange: sodiumRange ? sodiumRange.map(r => Math.round(r * minutes / 60)) : null,
      externalSodium,
      fluidRate,
      sodiumConcentration,
      sodiumTotal,
      fluidTotal: (simpleHydration ? sodiumRate / 600 : fluidRate) * minutes / 60,
      referenceHydration: !input.fluidRate && !input.sodiumConcentration,
      externalCaffeine,
      budget,
      remaining: Math.max(0, total - external),
      remainingLow: Math.max(0, totalLow - external),
      remainingHigh: Math.max(0, totalHigh - external),
      caffeineReviewThreshold: caffeineReviewThreshold({ weight }),
      fitBoundaryTolerance: Math.max(5, Math.round((minutes / 60) * 5)),
      notes
    };
  }

  function validProduct(p) {
    if (!p || !['fuel', 'hydration'].includes(p.role)) return false;
    if (p.verified !== true || p.available !== true || p.requiresSellingPlan) return false;
    if (!Number.isSafeInteger(Number(p.id)) || Number(p.id) <= 0) return false;

    if (![p.price, p.carbs, p.caffeine, p.sodium, p.servings].every(
      n => typeof n === 'number' && Number.isFinite(n)
    )) return false;

    if (p.price < 0 || p.carbs < 0 || p.carbs > 200 || p.caffeine < 0 || p.sodium < 0) return false;
    if (!Number.isInteger(p.servings) || p.servings < 1 || p.servings > 500) return false;

    if (![p.requiresWater, p.multiSource, p.vegan, p.glutenFree, p.dairyFree].every(validTriState)) {
      return false;
    }

    const maxServings = optionalPositiveInteger(p.maxServings);
    const maxServingsPerDay = optionalPositiveInteger(p.maxServingsPerDay);
    if (Number.isNaN(maxServings) || Number.isNaN(maxServingsPerDay)) return false;

    if (p.mixingMl != null && (!Number.isInteger(p.mixingMl) || p.mixingMl < 0)) return false;

    if (p.role === 'fuel') {
      if (!FUEL_FORMATS.has(p.format)) return false;
      if (p.carbs < 1) return false;
    }

    if (p.role === 'hydration') {
      if (!HYDRATION_FORMATS.has(p.format)) return false;
      if (p.sodium <= 0) return false;
      if (p.format === 'drinkmix' && !(Number.isInteger(p.mixingMl) && p.mixingMl > 0)) return false;
      // v4.11.8: chewable salt/electrolyte tablets can legitimately contain a
      // small amount of carbohydrate (for example SaltStick FastChews).
      // Keep capsule and generic `electrolyte` portions zero-carb, while the
      // hydration planner below continues to count tablet carbs against the
      // athlete's carbohydrate range exactly like drink-mix carbs.
      if (p.carbs > 0 && !['drinkmix', 'tablet'].includes(p.format)) return false;
    }

    return true;
  }

  function eligible(products, input, plan) {
    return products.filter(p =>
      validProduct(p) &&
      p.role === 'fuel' &&
      (input.brand === 'any' || p.brand === input.brand) &&
      (input.format === 'any' || p.format === input.format) &&
      (input.caffeine === 'yes' || p.caffeine === 0) &&
      (
        input.diet === 'none' ||
        (input.diet === 'vegan' && p.vegan === true) ||
        (input.diet === 'gluten' && p.glutenFree === true) ||
        (input.diet === 'dairy' && p.dairyFree === true)
      ) &&
      (input.water === 'yes' || p.requiresWater === false) &&
      (plan.budget == null || pack(p, plan, input).price <= Math.round(plan.budget * 100))
    );
  }

  function pack(product, plan, input) {
    if (!validProduct(product)) {
      throw new Error('This product has not been verified for planning.');
    }

    const range = planCarbRange(plan);
    let servings = 0;

    if (range.remainingLow > 0) {
      const maxByRange = Math.ceil(Math.max(range.remainingHigh, range.remaining, product.carbs) / product.carbs) + 2;
      const maxSearch = Math.max(1, Math.min(500, maxByRange));
      let best = null;

      for (let candidate = 1; candidate <= maxSearch; candidate++) {
        const carbs = candidate * product.carbs;
        const metrics = carbRangeMetrics(plan, carbs);
        // Being outside the planning range is penalised more heavily than
        // distance from the personalised starting aim, but a very small miss
        // can still beat a large overshoot caused by forcing another serving.
        const score = metrics.rangeDistance * 3 + metrics.centerDistance;
        const overshootPenalty = metrics.above * 0.25;
        const candidateScore = score + overshootPenalty;

        if (
          !best ||
          candidateScore < best.score ||
          (candidateScore === best.score && metrics.centerDistance < best.centerDistance) ||
          (candidateScore === best.score && metrics.centerDistance === best.centerDistance && candidate < best.servings)
        ) {
          best = {
            servings: candidate,
            score: candidateScore,
            centerDistance: metrics.centerDistance
          };
        }
      }

      servings = best?.servings || 1;
    }

    const ownedUsed = product.format === 'gel'
      ? Math.min(servings, plan.ownedGels || 0)
      : 0;

    const toBuy = servings - ownedUsed;
    const units = Math.ceil(toBuy / product.servings);
    const carbs = servings * product.carbs;
    const caffeine = servings * product.caffeine;
    const sodium = servings * product.sodium;
    const totalCaffeine = caffeine + (plan.externalCaffeine || 0);
    const warnings = [];
    const metrics = carbRangeMetrics(plan, carbs);
    const difference = metrics.combined - metrics.total;
    const limit = servingLimit(product);
    const boundaryTolerance = Number(plan.fitBoundaryTolerance || Math.max(5, Math.round((plan.minutes / 60) * 5)));
    const rangeAcceptable = metrics.rangeDistance <= boundaryTolerance;
    const caffeineThreshold = caffeineReviewThreshold(plan);

    if (!rangeAcceptable && servings > 0) {
      warnings.push(
        metrics.below > 0
          ? `This choice lands ${Math.round(metrics.below)}g below the carbohydrate planning range.`
          : `This choice lands ${Math.round(metrics.above)}g above the carbohydrate planning range.`
      );
    }

    if (limit != null && servings > limit) {
      warnings.push(
        `This plan requires ${servings} servings, above the verified product limit of ${servingLimitText(product)}. Choose another product or revise the plan.`
      );
    }

    if (metrics.achievedRate > 60.01 && product.multiSource !== true) {
      warnings.push(
        `This plan would average ${metrics.achievedRate.toFixed(1)} g/hour using a product that is not verified as a multiple-transportable-carbohydrate source. Choose a verified multi-source option for intake above 60 g/hour.`
      );
    }

    if (totalCaffeine > caffeineThreshold) {
      warnings.push(
        `Known caffeine across the recommended fuel and other sources exceeds the planner’s ${caffeineThreshold} mg review reference${plan.weight ? ` (~3 mg/kg at ${plan.weight} kg)` : ''}. Review your complete caffeine plan; this is not a personalised safety limit.`
      );
    }

    if (input.access === 'no' && servings) {
      warnings.push('Confirm during-session fuel access before buying a timed pack.');
    }

    const interval = servings ? plan.minutes / (servings + 1) : 0;
    if (interval > 0 && interval < 10) {
      warnings.push('This choice requires servings less than 10 minutes apart. Choose a more practical fuel format or serving size.');
    }

    const times = Array.from(
      { length: servings },
      (_, i) => Math.round((i + 1) * interval)
    );

    return {
      product,
      servings,
      ownedUsed,
      toBuy,
      units,
      carbs,
      caffeine,
      totalCaffeine,
      sodium,
      difference,
      times,
      price: units * product.price,
      leftover: units * product.servings - toBuy,
      totalCarbs: metrics.combined,
      achievedRate: metrics.achievedRate,
      rangeDistance: metrics.rangeDistance,
      centerDistance: metrics.centerDistance,
      withinRange: metrics.withinRange,
      rangeAcceptable,
      warnings,
      canCart: units > 0 && warnings.length === 0
    };
  }

  function sodiumRangeMetrics(plan, sodiumFromProducts = 0) {
    const low = Array.isArray(plan.sodiumTotalRange)
      ? Number(plan.sodiumTotalRange[0] || 0)
      : Number(plan.sodiumTotal || 0);
    const high = Array.isArray(plan.sodiumTotalRange)
      ? Number(plan.sodiumTotalRange[1] || low)
      : Number(plan.sodiumTotal || low);
    const center = (low + high) / 2;
    const combined = Math.max(0, Number(plan.externalSodium || 0)) +
      Math.max(0, Number(sodiumFromProducts || 0));
    const below = Math.max(0, low - combined);
    const above = Math.max(0, combined - high);
    return {
      low,
      high,
      center,
      combined,
      below,
      above,
      rangeDistance: below + above,
      centerDistance: Math.abs(combined - center),
      withinRange: below === 0 && above === 0
    };
  }

  function fuelRecommendationScore(pack, plan, input, recommended = true) {
    if (!pack || !plan) {
      return {
        score: 1000000000,
        carbTier: 9,
        sodiumTier: 9,
        carbScore: 0,
        sodiumScore: 0,
        sodiumMetrics: sodiumRangeMetrics(plan || {}, 0)
      };
    }

    const carbRangeDistance = Math.max(0, Number(pack.rangeDistance || 0));
    const carbCenterDistance = Math.max(0, Number(pack.centerDistance || 0));

    // Carbohydrate is the gate.
    let carbTier;
    if (!recommended) {
      carbTier = 2;
    } else if (carbRangeDistance === 0) {
      carbTier = 0;
    } else {
      carbTier = 1;
    }

    const sodiumMetrics = sodiumRangeMetrics(plan, Number(pack.sodium || 0));

    // Within the same carb tier, sodium becomes the main differentiator.
    let sodiumTier = 0;
    let sodiumDistanceRatio = 0;

    // Preserve v4.12.2 context sensitivity without changing the tier order.
    // This only strengthens sodium-distance influence inside an equal carb tier.
    let sodiumWeight = 1;
    if (input?.conditions === 'hot') sodiumWeight += 0.15;
    if (input?.sweat === 'heavy') sodiumWeight += 0.15;
    if ((input?.sodiumFormat || 'none') === 'none') sodiumWeight += 0.25;

    if (sodiumMetrics.high > 0) {
      if (sodiumMetrics.withinRange) {
        sodiumTier = 0;
        const halfRange = Math.max(1, (sodiumMetrics.high - sodiumMetrics.low) / 2);
        sodiumDistanceRatio = (sodiumMetrics.centerDistance / halfRange) * 0.10;
      } else if (sodiumMetrics.below > 0) {
        sodiumTier = 1;
        sodiumDistanceRatio = sodiumMetrics.below / Math.max(1, sodiumMetrics.low);
      } else {
        sodiumTier = 2;
        sodiumDistanceRatio = sodiumMetrics.above / Math.max(1, sodiumMetrics.high);
      }
    }

    let score = 0;

    // Lexicographic-style ordering.
    score += carbTier * 100000000;
    score += sodiumTier * 1000000;
    score += sodiumDistanceRatio * 100000 * sodiumWeight;

    // Lower-order practicality / price.
    score += Math.max(0, Number(pack.servings || 0)) * 20;
    score += Math.max(0, Number(pack.units || 0)) * 50;
    score += Math.max(0, Number(pack.price || 0)) / 100;

    // Tiny final tie-breaker only.
    score += carbCenterDistance;

    return {
      score,
      carbTier,
      sodiumTier,
      carbScore: carbTier * 100000000 + carbCenterDistance,
      sodiumScore: sodiumTier * 1000000 + sodiumDistanceRatio * 100000 * sodiumWeight,
      sodiumWeight,
      sodiumMetrics
    };
  }

  function hydration(products, input, plan, fuel) {
    const preference = input.sodiumFormat || 'none';
    const baseFuelSodium = fuel?.sodium || 0;
    const baseMetrics = sodiumRangeMetrics(plan, baseFuelSodium);
    const baseGap = Math.max(0, baseMetrics.low - baseMetrics.combined);

    if (preference === 'none' || baseGap === 0) {
      return {
        gap: baseGap,
        fuelSodium: baseFuelSodium,
        externalSodium: plan.externalSodium,
        target: plan.sodiumTotal,
        targetRange: [baseMetrics.low, baseMetrics.high],
        choices: [],
        selected: null,
        referenceTablets: Math.ceil(baseGap / 200),
        referenceElectrolytes: Math.ceil(baseGap / 500),
        total: baseMetrics.combined,
        sodiumMetrics: baseMetrics
      };
    }

    const pool = products.filter(p =>
      validProduct(p) &&
      p.role === 'hydration' &&
      HYDRATION_FORMATS.has(p.format) &&
      (preference === 'any' || preference === p.format) &&
      (input.caffeine === 'yes' || p.caffeine === 0) &&
      input.water === 'yes' &&
      (
        !['drinkmix', 'electrolyte'].includes(p.format) ||
        input.carryBottle !== 'no'
      ) &&
      (
        input.diet === 'none' ||
        (input.diet === 'vegan' && p.vegan === true) ||
        (input.diet === 'gluten' && p.glutenFree === true) ||
        (input.diet === 'dairy' && p.dairyFree === true)
      )
    );

    const choices = [];

    for (const product of pool) {
      const limit = servingLimit(product);
      const maxLoop = Math.min(120, limit || 120);
      let best = null;

      for (let servings = 1; servings <= maxLoop; servings++) {
        const carbs = servings * product.carbs;
        const adjustedFuel = fuel
          ? pack(
              fuel.product,
              {
                ...plan,
                remaining: Math.max(0, plan.remaining - carbs),
                external: plan.external + carbs
              },
              input
            )
          : null;

        if (adjustedFuel?.warnings.length) continue;

        const fuelSodium = adjustedFuel?.sodium || 0;
        const sodium = servings * product.sodium;
        const units = Math.ceil(servings / product.servings);
        const price = units * product.price;
        const mixingMl = product.mixingMl == null ? null : servings * product.mixingMl;
        const combinedFuelCarbs = carbs + (adjustedFuel?.carbs || 0);
        const carbMetrics = carbRangeMetrics(plan, combinedFuelCarbs);
        const sodiumMetrics = sodiumRangeMetrics(plan, fuelSodium + sodium);
        const carbValid = product.carbs === 0 ||
          carbMetrics.rangeDistance <= Number(plan.fitBoundaryTolerance || 5);
        const mixingValid = mixingMl == null || mixingMl <= plan.fluidTotal * 1000;
        const knownCaffeine = (adjustedFuel?.totalCaffeine || plan.externalCaffeine || 0) +
          servings * product.caffeine;
        const caffeineThreshold = caffeineReviewThreshold(plan);
        const highRateNeedsMultiSource = carbMetrics.achievedRate > 60.01 &&
          product.carbs > 0 &&
          product.multiSource !== true &&
          adjustedFuel?.product?.multiSource !== true;

        if (!mixingValid) continue;
        if (!carbValid) continue;
        if (highRateNeedsMultiSource) continue;
        if (knownCaffeine > caffeineThreshold) continue;
        if (plan.budget != null && price + (adjustedFuel?.price || 0) > plan.budget * 100) continue;

        // Sodium is now a true second replenishment target. Prefer quantities
        // inside its full-session range; if serving granularity makes that
        // impossible, keep the closest defensible quantity rather than forcing
        // the old "at least the midpoint" rule.
        const sodiumSpan = Math.max(1, sodiumMetrics.high - sodiumMetrics.low);
        const sodiumPenalty = sodiumMetrics.rangeDistance === 0
          ? sodiumMetrics.centerDistance / sodiumSpan
          : 10 + sodiumMetrics.rangeDistance / sodiumSpan;
        const carbPenalty = carbMetrics.rangeDistance * 3 + carbMetrics.centerDistance;
        const score = sodiumPenalty * 100 + carbPenalty + price / 100000;

        const candidate = {
          product,
          servings,
          units,
          sodium,
          carbs,
          caffeine: servings * product.caffeine,
          price,
          mixingMl,
          fuel: adjustedFuel,
          valid: true,
          sodiumMetrics,
          score
        };

        if (!best || candidate.score < best.score) best = candidate;

        // Once quantities have moved beyond the upper sodium range and are
        // getting farther away, later servings cannot improve sodium fit.
        if (sodiumMetrics.above > 0 && best?.sodiumMetrics?.withinRange) break;
      }

      if (best) choices.push(best);
    }

    choices.sort(
      (a, b) =>
        Number(b.sodiumMetrics?.withinRange) - Number(a.sodiumMetrics?.withinRange) ||
        a.score - b.score ||
        (a.price + (a.fuel?.price || 0)) - (b.price + (b.fuel?.price || 0))
    );

    const selected = choices.find(x => String(x.product.id) === String(input.hydrationId)) ||
      choices[0] || null;
    const fuelSodium = (selected?.fuel || fuel)?.sodium || 0;
    const selectedMetrics = sodiumRangeMetrics(
      plan,
      fuelSodium + (selected?.sodium || 0)
    );
    const gap = Math.max(0, selectedMetrics.low - selectedMetrics.combined);

    return {
      gap,
      fuelSodium,
      externalSodium: plan.externalSodium,
      target: plan.sodiumTotal,
      targetRange: [selectedMetrics.low, selectedMetrics.high],
      choices,
      selected,
      referenceTablets: Math.ceil(gap / 200),
      referenceElectrolytes: Math.ceil(gap / 500),
      total: selectedMetrics.combined,
      sodiumMetrics: selectedMetrics
    };
  }

  const api = {
    calculate,
    validProduct,
    eligible,
    pack,
    hydration,
    servingLimit,
    servingLimitText,
    planCarbRange,
    carbRangeMetrics,
    sodiumRangeMetrics,
    fuelRecommendationScore,
    caffeineReviewThreshold
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  global.RacepackFuelEngine = api;
})(typeof window !== 'undefined' ? window : globalThis);
